import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Gift, Copy, Plus, Sparkles, Calendar, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standalone "My Gift Cards" page. Also rendered as a tab inside MyLoyalty
// (see <GiftCardsList /> export below).
export const MyGiftCards = () => {
  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>My Gift Cards</h1>
            <p className="text-[#6B6B7A] mt-1 text-sm">Cards you've purchased and gifts you've received.</p>
          </div>
          <Link to="/gift-cards"><Button className="bg-[#FF3B6B] hover:bg-[#E62E58] rounded-full text-white" data-testid="my-gc-buy-btn"><Plus className="w-4 h-4 mr-1" /> Buy a gift card</Button></Link>
        </div>
        <GiftCardsList />
      </div>
    </div>
  );
};

export const GiftCardsList = ({ cards: cardsProp, onChanged } = {}) => {
  const controlled = cardsProp !== undefined;
  const [cardsState, setCardsState] = useState([]);
  const [loading, setLoading] = useState(!controlled);
  const [sendCard, setSendCard] = useState(null);
  const cards = controlled ? cardsProp : cardsState;

  const fetchCards = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/customer/gift-cards`, { withCredentials: true });
      setCardsState(r.data || []);
    } catch (e) {
      if (e.response?.status === 401) window.location.href = '/login';
      else toast.error('Failed to load gift cards');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (!controlled) fetchCards(); }, [controlled]);

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`${code} copied`); };

  const openPdf = async (code, download) => {
    const w = download ? null : window.open('', '_blank');
    try {
      const r = await axios.get(`${API_URL}/api/customer/gift-cards/${code}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `creative-birthdays-gift-${code}.pdf`;
        a.click();
      } else if (w) {
        w.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (w) w.close();
      toast.error(e.response?.data?.detail || 'Could not open PDF');
    }
  };

  if (loading) return <div className="text-center py-10 text-[#6B6B7A]" data-testid="my-gc-loading">Loading…</div>;

  if (!cards.length) {
    return (
      <div className="bg-white border-2 border-dashed border-[#E8E5DF] rounded-2xl p-10 text-center" data-testid="my-gc-empty">
        <Gift className="w-12 h-12 mx-auto text-[#FF3B6B]/40 mb-3" />
        <h3 className="font-semibold text-[#1E1E24] mb-1">No gift cards yet</h3>
        <p className="text-sm text-[#6B6B7A] mb-4">Purchase a gift card or receive one — they'll show up here.</p>
        <Link to="/gift-cards"><Button className="bg-[#FF3B6B] hover:bg-[#E62E58] rounded-full text-white"><Plus className="w-4 h-4 mr-1" /> Buy your first gift card</Button></Link>
      </div>
    );
  }

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="my-gc-list">
        {cards.map((c) => {
          const isActive = c.status === 'active';
          return (
            <div key={c.gift_card_id} className="bg-white border border-[#E8E5DF] rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`gc-row-${c.code}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs text-[#6B6B7A] uppercase tracking-widest">{c.role === 'recipient' ? 'Received' : 'Purchased'}</p>
                  <p className="text-3xl font-bold text-[#FF3B6B] mt-0.5">${c.amount.toFixed(0)}</p>
                  <p className="text-xs text-[#6B6B7A] mt-1">Balance: <b className="text-[#1E1E24]">${(c.balance_remaining || 0).toFixed(2)}</b></p>
                </div>
                <StatusPill status={c.status} balance={c.balance_remaining} expires={c.expires_at} />
              </div>
              <div className="bg-[#FFFDF8] rounded-xl p-3 mb-3 text-center">
                <p className="text-[10px] text-[#6B6B7A] tracking-widest mb-1">CODE</p>
                <p className="text-xl font-bold font-mono text-[#1E1E24] tracking-widest">{c.code}</p>
              </div>
              <p className="text-xs text-[#6B6B7A] mb-3">
                {c.role === 'recipient' ? (
                  <>From <b>{c.purchaser_name || 'A friend'}</b> · {new Date(c.purchased_at).toLocaleDateString()}</>
                ) : (
                  <>To <b>{c.recipient_name}</b> · {new Date(c.purchased_at).toLocaleDateString()}</>
                )}
              </p>
              <div className="flex items-center gap-2 text-xs text-[#6B6B7A] mb-3">
                <Calendar className="w-3 h-3" />
                <span>Expires {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => copy(c.code)} className="min-h-[40px]" data-testid={`gc-copy-${c.code}`}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                {isActive && (
                  <Button size="sm" variant="outline" onClick={() => openPdf(c.code, false)} className="min-h-[40px]" data-testid={`gc-view-${c.code}`}><ExternalLink className="w-3 h-3 mr-1" /> View</Button>
                )}
                {isActive && (c.balance_remaining || 0) > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setSendCard(c)} className="min-h-[40px]" data-testid={`gc-send-${c.code}`}><Send className="w-3 h-3 mr-1" /> Send to friend</Button>
                )}
                {(c.balance_remaining || 0) > 0 && isActive && (
                  <Link to={`/book?gift_card=${c.code}`}>
                    <Button size="sm" className="min-h-[40px] bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid={`gc-use-${c.code}`}><Sparkles className="w-3 h-3 mr-1" /> Use</Button>
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SendGiftCardDialog card={sendCard} onClose={() => setSendCard(null)} />
    </>
  );
};

const SendGiftCardDialog = ({ card, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { setEmail(''); setName(''); setMessage(''); }, [card]);

  const submit = async () => {
    if (!email.includes('@')) { toast.error('Enter a valid friend email'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/customer/gift-cards/${card.code}/send`,
        { email: email.trim(), name: name.trim(), message: message.trim() },
        { withCredentials: true });
      toast.success(`Gift card sent to ${email.trim()}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send gift card');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!card} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-white" data-testid="gc-send-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }}>Send gift card to a friend</DialogTitle>
          <DialogDescription>We'll email {card ? `$${card.amount?.toFixed?.(0)}` : 'this'} gift card (code {card?.code}) and its PDF voucher to your friend.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-semibold">Friend's email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@email.com" className="mt-1 rounded-xl" data-testid="gc-send-email" />
          </div>
          <div>
            <Label className="font-semibold">Friend's name <span className="text-[#9a9aa6] font-normal">(optional)</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan" className="mt-1 rounded-xl" data-testid="gc-send-name" />
          </div>
          <div>
            <Label className="font-semibold">Personal message <span className="text-[#9a9aa6] font-normal">(optional)</span></Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hope this makes your day!" rows={3} className="mt-1 rounded-xl" data-testid="gc-send-message" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-full" disabled={sending}>Cancel</Button>
            <Button onClick={submit} disabled={sending} className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="gc-send-submit">
              {sending ? 'Sending…' : 'Send gift card'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatusPill = ({ status, balance, expires }) => {
  const isExpired = expires && new Date(expires) < new Date();
  let label = status;
  let cls = 'bg-[#E8E5DF] text-[#6B6B7A]';
  if (status === 'active' && (balance || 0) <= 0) { label = 'Redeemed'; cls = 'bg-[#6B6B7A]/10 text-[#6B6B7A]'; }
  else if (status === 'active' && isExpired) { label = 'Expired'; cls = 'bg-[#FF3B6B]/10 text-[#FF3B6B]'; }
  else if (status === 'active') { label = 'Active'; cls = 'bg-[#06D6A0]/10 text-[#06D6A0]'; }
  else if (status === 'pending_payment') { label = 'Pending'; cls = 'bg-[#FB923C]/10 text-[#FB923C]'; }
  else if (status === 'cancelled') { label = 'Cancelled'; cls = 'bg-[#FF3B6B]/10 text-[#FF3B6B]'; }
  return <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase ${cls}`}>{label}</span>;
};

export default MyGiftCards;
